import { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ErrorBoundary } from 'react-error-boundary';
import client from 'client';
import { LoadingErrorPage, ThemeProvider } from '@bizzabo/design-system';
import { FeatureToggleProvider } from '@bizzabo/feature-toggle-client';
import { instanaReportError } from '@bizzabo/instana-api';

import { EventDetailsProvider } from './contexts/RingContext';
import { getEventDetailsByDomain } from './utils/EventNameUtil';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { init as initFeatureToggleClient } from './featureToggle';

import './index.css';
import { Event } from '@bizzabo/entities';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: Infinity,
        },
    },
});

// https://forum.sentry.io/t/resizeobserver-loop-limit-exceeded/8402/5
// eslint-disable-next-line
// @ts-ignore-next-line
window.ineum?.('ignoreErrorMessages', [
    /^ResizeObserver loop limit exceeded/i,
    /^script error/i,
    /^Non-Error promise rejection captured/i,
]);

const onError = (error: Error, info: { componentStack: string }) => {
    instanaReportError(error, {
        componentStack: info.componentStack,
    });
};

const getEvent = async (eventUniqueName: string) => {
    try {
        const {
            data: { event },
        } = await client().get<{ event: Event }>(`/api/v1/event/unique-name/${eventUniqueName}`);
        return event;
    } catch (e) {
        return undefined;
    }
};

(async () => {
    try {
        const { eventUniqueName, isEventWhiteLabel, whitelabelPath } =
            await getEventDetailsByDomain();
        const event = await getEvent(eventUniqueName);
        const accountId = event?.['account-id'] || undefined;
        const featureToggleClient = await initFeatureToggleClient(eventUniqueName, accountId);

        ReactDOM.render(
            <StrictMode>
                <ThemeProvider>
                    <ErrorBoundary FallbackComponent={LoadingErrorPage} onError={onError}>
                        <QueryClientProvider client={queryClient} contextSharing>
                            <EventDetailsProvider
                                eventUniqueName={eventUniqueName}
                                isEventWhiteLabel={isEventWhiteLabel}
                                whitelabelPath={whitelabelPath}
                            >
                                <FeatureToggleProvider client={featureToggleClient}>
                                    <App />
                                </FeatureToggleProvider>
                                <ReactQueryDevtools />
                            </EventDetailsProvider>
                        </QueryClientProvider>
                    </ErrorBoundary>
                </ThemeProvider>
            </StrictMode>,
            document.getElementById('root')
        );
    } catch (error) {
        instanaReportError(error);
        ReactDOM.render(
            <StrictMode>
                <ThemeProvider>
                    <LoadingErrorPage />
                </ThemeProvider>
            </StrictMode>,
            document.getElementById('root')
        );
    }

    // If you want to start measuring performance in your app, pass a function
    // to log results (for example: reportWebVitals(console.log))
    // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
    reportWebVitals();
})();
